import React from 'react';
import { observer } from 'mobx-react-lite';
import { ItemBlock, Tasklist, Title, ShowHide, ShowTask, SubTaskList, LineDiv, ShowSubTask } from '../../styles';
import TaskItem from '../../taskItem';
import { TaskModel } from '../../../../../mobx/models/task';
import { ProjectModel } from '../../../../../mobx/models/project';
import { ProjectTemplateModel } from '../../../../../mobx/models/projectTemplate';
import { StatusListInterface } from '../../../../../interfaces/ProjectInterface';
import {
  NewTaskFilterInterface,
  MobxFilteredCompletedTasksListInterface,
  SubtaskDataResult
} from '../../../../../interfaces/TaskInterface';
import { SHOW_TASK_GROUPING_TYPE } from '../../../../../global/constants';

interface Props {
  otherTask?: MobxFilteredCompletedTasksListInterface;
  groupingStatus?: number;
  loadData: () => void;
  isTemplateProject?: boolean;
  currentProject?: ProjectModel | ProjectTemplateModel | null;
  setOpen: (value: boolean) => void;
  setDeleteModalOpen: (value: boolean) => void;
  setSelectedItem: (value: TaskModel) => void;
  handleSubtaskOpen?: (id: string) => void;
  openSubTask?: { id: string; showCompleted: boolean };
  taskFilter?: NewTaskFilterInterface;
  onClickTaskCheckBox: (task: TaskModel) => void;
  onChangeStatus: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  isView?: boolean;
  showProjectName?: boolean;
  getSubtaskData: (task: TaskModel) => SubtaskDataResult;
  selectedId?: string;
  showCompletedTask?: boolean;
  handleShowCompletedSubTask: (id: string, isShow: boolean) => void;
  toggleCompletedTask: (value: boolean, id: string) => void;
  maxVisibleLabels: number;
}

const OtherTaskList = observer(function OtherTaskList(props: Props) {
  const {
    otherTask,
    groupingStatus,
    loadData,
    isTemplateProject,
    currentProject,
    setOpen,
    setDeleteModalOpen,
    setSelectedItem,
    handleSubtaskOpen,
    openSubTask,
    taskFilter,
    onClickTaskCheckBox,
    onChangeStatus,
    isView,
    showProjectName,
    getSubtaskData,
    selectedId,
    showCompletedTask,
    handleShowCompletedSubTask,
    toggleCompletedTask,
    maxVisibleLabels
  } = props;

  const handleShowSubtaskCompletedToggle = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const taskId = event.currentTarget.dataset.taskId || '';
      const showCompleted = event.currentTarget.dataset.showCompleted === 'true';

      handleShowCompletedSubTask(taskId, showCompleted);
    },
    [handleShowCompletedSubTask]
  );

  const handleHideOtherCompleted = React.useCallback(() => toggleCompletedTask(false, ''), [toggleCompletedTask]);

  const handleShowOtherCompleted = React.useCallback(
    () => toggleCompletedTask(true, 'otherTasks'),
    [toggleCompletedTask]
  );

  if (!otherTask?.tasks?.length && !otherTask?.completedTasks?.length) return null;

  return (
    <ItemBlock isHide={groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY}>
      <Tasklist
        isGap={groupingStatus === SHOW_TASK_GROUPING_TYPE.NO_GROUPING}
        isHide={groupingStatus === SHOW_TASK_GROUPING_TYPE.NO_GROUPING}>
        <Title>Other Tasks</Title>
      </Tasklist>
      {otherTask?.tasks?.map((task) => {
        const { visibleSubtasks, completedSubtasks } = getSubtaskData(task) || {};
        return (
          <React.Fragment key={task?.id}>
            <TaskItem
              currentProject={currentProject!}
              loadData={loadData}
              onClickTaskCheckBox={onClickTaskCheckBox}
              onChangeStatus={onChangeStatus}
              task={task}
              isOtherTask
              isTemplateProject={isTemplateProject}
              setOpen={setOpen}
              setDeleteModelopen={setDeleteModalOpen}
              setSelectedItem={setSelectedItem}
              handleSubtaskOpen={handleSubtaskOpen}
              isActiveSubTask={openSubTask?.id === task?.id}
              isView={isView}
              showProjectName={showProjectName}
              maxVisibleLabels={maxVisibleLabels}
            />

            {task?.subTasks &&
              task?.subTasks?.length > 0 &&
              ((taskFilter?.showSubTask && !isView) || openSubTask?.id === task?.id) &&
              !isView && (
                <ShowSubTask>
                  <LineDiv isApplyMargin={completedSubtasks && completedSubtasks?.length > 0} />
                  <SubTaskList>
                    {visibleSubtasks?.map((sub) => {
                      const item: TaskModel = sub;
                      return (
                        <TaskItem
                          key={item?.id}
                          isSubTask={true}
                          currentProject={sub?.project!}
                          loadData={loadData}
                          onClickTaskCheckBox={onClickTaskCheckBox}
                          onChangeStatus={onChangeStatus}
                          task={item}
                          isOtherTask
                          isTemplateProject={isTemplateProject}
                          setOpen={setOpen}
                          setDeleteModelopen={setDeleteModalOpen}
                          setSelectedItem={setSelectedItem}
                          isView={isView}
                          showProjectName={showProjectName}
                          maxVisibleLabels={maxVisibleLabels}
                        />
                      );
                    })}
                    {completedSubtasks && completedSubtasks?.length > 0 && (
                      <>
                        {(!openSubTask?.showCompleted || openSubTask?.id !== task?.id) && (
                          <ShowHide
                            role='button'
                            showSubTask={true}
                            data-task-id={task?.id}
                            data-show-completed='true'
                            onClick={handleShowSubtaskCompletedToggle}>
                            Show Completed Tasks
                          </ShowHide>
                        )}
                        {openSubTask?.showCompleted && openSubTask?.id === task?.id && (
                          <ShowHide
                            role='button'
                            showSubTask={true}
                            data-task-id={task?.id}
                            data-show-completed='false'
                            onClick={handleShowSubtaskCompletedToggle}>
                            Hide Completed Tasks
                          </ShowHide>
                        )}
                      </>
                    )}
                  </SubTaskList>
                </ShowSubTask>
              )}
          </React.Fragment>
        );
      })}

      {otherTask?.completedTasks && otherTask?.completedTasks?.length > 0 && (
        <ShowTask>
          {selectedId === 'otherTasks' && showCompletedTask ? (
            <ShowHide role='button' onClick={handleHideOtherCompleted}>
              Hide Completed Tasks
            </ShowHide>
          ) : (
            <ShowHide role='button' onClick={handleShowOtherCompleted}>
              Show Completed Tasks
            </ShowHide>
          )}

          {selectedId === 'otherTasks' && showCompletedTask && otherTask?.completedTasks?.length > 0 && (
            <>
              {otherTask?.completedTasks?.map((task) => {
                const { visibleSubtasks, completedSubtasks } = getSubtaskData(task) || {};
                return (
                  <React.Fragment key={task?.id}>
                    <TaskItem
                      currentProject={currentProject!}
                      loadData={loadData}
                      onClickTaskCheckBox={onClickTaskCheckBox}
                      onChangeStatus={onChangeStatus}
                      task={task}
                      isOtherTask
                      isTemplateProject={isTemplateProject}
                      setOpen={setOpen}
                      setDeleteModelopen={setDeleteModalOpen}
                      setSelectedItem={setSelectedItem}
                      handleSubtaskOpen={handleSubtaskOpen}
                      isActiveSubTask={openSubTask?.id === task?.id}
                      isView={isView}
                      showProjectName={showProjectName}
                      maxVisibleLabels={maxVisibleLabels}
                    />

                    {task?.subTasks &&
                      task?.subTasks?.length > 0 &&
                      ((taskFilter?.showSubTask && !isView) || openSubTask?.id === task?.id) && (
                        <ShowSubTask>
                          <LineDiv isApplyMargin={completedSubtasks && completedSubtasks?.length > 0} />
                          <SubTaskList>
                            {visibleSubtasks?.map((sub) => {
                              const item: TaskModel = sub;
                              return (
                                <TaskItem
                                  key={item?.id}
                                  isSubTask={true}
                                  currentProject={sub?.project!}
                                  loadData={loadData}
                                  onClickTaskCheckBox={onClickTaskCheckBox}
                                  onChangeStatus={onChangeStatus}
                                  task={item}
                                  isOtherTask
                                  isTemplateProject={isTemplateProject}
                                  setOpen={setOpen}
                                  setDeleteModelopen={setDeleteModalOpen}
                                  setSelectedItem={setSelectedItem}
                                  isView={isView}
                                  showProjectName={showProjectName}
                                  maxVisibleLabels={maxVisibleLabels}
                                />
                              );
                            })}
                            {completedSubtasks && completedSubtasks?.length > 0 && (
                              <>
                                {(!openSubTask?.showCompleted || openSubTask?.id !== task?.id) && (
                                  <ShowHide
                                    role='button'
                                    showSubTask={true}
                                    data-task-id={task?.id}
                                    data-show-completed='true'
                                    onClick={handleShowSubtaskCompletedToggle}>
                                    Show Completed Tasks
                                  </ShowHide>
                                )}
                                {openSubTask?.showCompleted && openSubTask?.id === task?.id && (
                                  <ShowHide
                                    role='button'
                                    showSubTask={true}
                                    data-task-id={task?.id}
                                    data-show-completed='false'
                                    onClick={handleShowSubtaskCompletedToggle}>
                                    Hide Completed Tasks
                                  </ShowHide>
                                )}
                              </>
                            )}
                          </SubTaskList>
                        </ShowSubTask>
                      )}
                  </React.Fragment>
                );
              })}
            </>
          )}
        </ShowTask>
      )}
    </ItemBlock>
  );
});

export default OtherTaskList;
